/**
 * Run one request per record, a few at a time, and report each record's outcome (SF-4875,
 * SF-4940). There are no bulk routes behind the admin app, so batch actions on the Users and
 * Companies lists send one request per record.
 */

/** Requests in flight at once: quick for a page of records without flooding the service. */
const CONCURRENCY = 4;

export interface BatchFailure {
  id: string;
  message: string;
}

export interface BatchResult {
  /** Records that were changed. */
  updated: string[];
  /** Records left as they were because nothing needed to change. */
  unchanged: string[];
  failed: BatchFailure[];
}

/**
 * The sign-in service answers `{ error: { message } }`; fall back to a plain `message`
 * and then to the network error text.
 */
export const batchErrorMessage = (err: unknown): string => {
  const e = err as {
    response?: { data?: { error?: { message?: unknown }; message?: unknown } };
    message?: unknown;
  };
  const candidates = [e?.response?.data?.error?.message, e?.response?.data?.message, e?.message];
  const found = candidates.find((m) => typeof m === 'string' && m.trim());
  return (found as string | undefined) ?? 'Unknown error';
};

export type BatchOutcome = 'updated' | 'unchanged';

/** Runs `work` for every id, a few at a time, and sorts each into updated/unchanged/failed. */
export const runBatch = async (
  ids: string[],
  work: (id: string) => Promise<BatchOutcome>,
  concurrency = CONCURRENCY,
  errorMessage: (err: unknown) => string = batchErrorMessage,
): Promise<BatchResult> => {
  const result: BatchResult = { updated: [], unchanged: [], failed: [] };
  const queue = [...ids];
  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        result[await work(id)].push(id);
      } catch (err) {
        result.failed.push({ id, message: errorMessage(err) });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, ids.length) }, worker));
  return result;
};
